import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { factoryApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Package, Scale, Gem, Calendar, Clock,
  Loader2, MoveRight, History, AlertTriangle, MessageSquare, Plus, X, Send,
  CheckCircle2, Flame, Link2, CircleDot
} from 'lucide-react';
import { cn } from '../lib/utils';
import HomeButton from '../components/HomeButton';

const REQUEST_TYPES = [
  'Waiting for Metal',
  'Waiting for Diamonds',
  'Waiting for Chains',
  'Request New Direct Casting Piece(s)',
  'Delay Expected',
  'Other',
];
const FACTORY_SUB_STAGES = ['On the Floor', 'Filing', 'Pre Polish', 'Setting', 'Polishing', 'Finished Goods'];
const ALL_FACTORY_STAGES = ['Order Raised', 'Casting', 'On the Floor', 'Filing', 'Pre Polish', 'Setting', 'Polishing', 'Finished Goods', 'Dispatched'];

// Display label override — backend keeps "Order Raised" as the stage key
// (so legacy data + workflow are untouched), but the factory portal surfaces
// it as "Order Accepted" since by definition pieces only reach this stage
// after the factory has accepted the order.
const STAGE_LABEL = {
  'Order Raised': 'Order Accepted',
};
const STAGE_COLORS = {
  'Order Raised': { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300', ring: 'ring-slate-300 dark:ring-slate-600' },
  'Casting': { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', ring: 'ring-amber-300 dark:ring-amber-500/30' },
  'On the Floor': { bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', ring: 'ring-blue-300 dark:ring-blue-500/30' },
  'Filing': { bg: 'bg-indigo-50 dark:bg-indigo-500/10', text: 'text-indigo-700 dark:text-indigo-400', ring: 'ring-indigo-300 dark:ring-indigo-500/30' },
  'Pre Polish': { bg: 'bg-purple-50 dark:bg-purple-500/10', text: 'text-purple-700 dark:text-purple-400', ring: 'ring-purple-300 dark:ring-purple-500/30' },
  'Setting': { bg: 'bg-pink-50 dark:bg-pink-500/10', text: 'text-pink-700 dark:text-pink-400', ring: 'ring-pink-300 dark:ring-pink-500/30' },
  'Polishing': { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', ring: 'ring-emerald-300 dark:ring-emerald-500/30' },
  'Finished Goods': { bg: 'bg-green-50 dark:bg-green-500/10', text: 'text-green-700 dark:text-green-400', ring: 'ring-green-300 dark:ring-green-500/30' },
  'Dispatched': { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-800 dark:text-emerald-300', ring: 'ring-emerald-400' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { const d = new Date(iso); return `${d.getDate()} ${d.toLocaleString('en-IN',{month:'short'})} ${d.getFullYear()}`; } catch { return ''; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try { const d = new Date(iso); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; } catch { return ''; }
};
const fmtDeadline = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diffH = Math.round((d - new Date()) / 3600000);
    if (diffH < 0) return { text: `${Math.abs(diffH)}h overdue`, status: 'overdue' };
    if (diffH < 6) return { text: `${diffH}h left`, status: 'at_risk' };
    if (diffH < 48) return { text: `${diffH}h left`, status: 'on_track' };
    return { text: fmtDate(iso), status: 'on_track' };
  } catch { return null; }
};

// ─── Material Status Card ───
const MaterialCard = ({ label, received, icon: Icon, color }) => (
  <div className={cn(
    "flex items-center gap-3 p-3 rounded-lg border",
    received === true ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/5 dark:border-emerald-500/20" :
    received === false ? "bg-red-50 border-red-200 dark:bg-red-500/5 dark:border-red-500/20" :
    "bg-muted/30 border-border"
  )} data-testid={`material-${label.toLowerCase()}`}>
    <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center",
      received === true ? "bg-emerald-100 dark:bg-emerald-500/20" :
      received === false ? `bg-${color}-100 dark:bg-${color}-500/20` :
      "bg-muted"
    )}>
      <Icon className={cn("h-4 w-4",
        received === true ? "text-emerald-600 dark:text-emerald-400" :
        received === false ? `text-${color}-600 dark:text-${color}-400` :
        "text-muted-foreground"
      )} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn("text-sm font-semibold",
        received === true ? "text-emerald-600 dark:text-emerald-400" :
        received === false ? "text-red-600 dark:text-red-400" :
        "text-muted-foreground"
      )}>
        {received === true ? 'Received' : received === false ? 'Pending' : 'Not Required'}
      </p>
    </div>
    {received === true ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
     received === false ? <Clock className="h-5 w-5 text-red-400 animate-pulse" /> : null}
  </div>
);

// ─── Deadline Banner ───
const DeadlineBanner = ({ label, iso, completed }) => {
  if (completed) return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 dark:bg-emerald-500/5 dark:border-emerald-500/20">
      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{label} — Complete</span>
    </div>
  );
  const dl = fmtDeadline(iso);
  if (!dl) return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
      <Clock className="h-4 w-4 text-muted-foreground" />
      <span className="text-xs text-muted-foreground">{label} — Not started</span>
    </div>
  );
  const cfg = {
    overdue: { bg: 'bg-red-50 border-red-200 dark:bg-red-500/5 dark:border-red-500/20', text: 'text-red-600 dark:text-red-400', icon: Flame },
    at_risk: { bg: 'bg-amber-50 border-amber-200 dark:bg-amber-500/5 dark:border-amber-500/20', text: 'text-amber-600 dark:text-amber-400', icon: AlertTriangle },
    on_track: { bg: 'bg-blue-50 border-blue-200 dark:bg-blue-500/5 dark:border-blue-500/20', text: 'text-blue-600 dark:text-blue-400', icon: CircleDot },
  }[dl.status];
  const DlIcon = cfg.icon;
  return (
    <div className={cn("flex items-center justify-between p-2.5 rounded-lg border", cfg.bg)} data-testid={`deadline-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <DlIcon className={cn("h-4 w-4", cfg.text)} />
        <span className={cn("text-xs font-semibold", cfg.text)}>{label}</span>
      </div>
      <span className={cn("text-xs font-bold tabular-nums", cfg.text)}>{dl.text}</span>
    </div>
  );
};

// Manufacturing Order Form / Factory Processing Sheet download — shown to the
// factory on the pending-acceptance card and the post-acceptance production view.
const _FactoryFileRow = ({ mf, url, label }) => {
  if (!mf) return null;
  const sizeKb = mf.size ? Math.max(1, Math.round(mf.size / 1024)) : null;
  const sizeText = sizeKb && sizeKb < 1024 ? `${sizeKb} KB` : (mf.size ? `${(mf.size / 1024 / 1024).toFixed(2)} MB` : '');
  const ts = mf.uploaded_at ? `${fmtDate(mf.uploaded_at)} ${fmtTime(mf.uploaded_at)}` : '';
  return (
    <div className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0 border-b last:border-b-0 border-amber-100 dark:border-amber-500/20">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold truncate">{mf.filename || 'file'}</p>
        <p className="text-[11px] text-muted-foreground">
          Uploaded by {mf.uploaded_by_name || 'admin'}{ts ? ` · ${ts}` : ''}{sizeText ? ` · ${sizeText}` : ''}
        </p>
      </div>
      <Button
        size="sm"
        onClick={() => window.open(url, '_blank', 'noopener')}
        className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
        data-testid={`factory-file-download-${label.toLowerCase().replace(/\s+/g,'-')}`}
      >
        <ArrowRight className="h-4 w-4 mr-1" /> Open
      </Button>
    </div>
  );
};

const ManufacturingFormDownload = ({ mf, orderId, variant = 'inline', sheet }) => {
  if (!mf && !sheet) return null;
  const token = encodeURIComponent(localStorage.getItem('siddhi_token') || '');
  const mfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/manufacturing-form/file?token=${token}`;
  const sheetUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/factory-processing-sheet/file?token=${token}`;

  if (variant === 'banner') {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-white/70 dark:bg-background/40 p-3 space-y-1" data-testid="factory-files-banner">
        <_FactoryFileRow mf={mf} url={mfUrl} label="Manufacturing Order Form" />
        <_FactoryFileRow mf={sheet} url={sheetUrl} label="Factory Processing Sheet" />
      </div>
    );
  }
  // inline (post-acceptance)
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="factory-files-inline">
      {mf && (
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/5">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Manufacturing Order Form</p>
            <p className="text-sm font-medium truncate">{mf.filename || 'form'}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => window.open(mfUrl, '_blank', 'noopener')} data-testid="factory-mfg-form-download-inline-btn">
            Download
          </Button>
        </div>
      )}
      {sheet && (
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/5">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Factory Processing Sheet</p>
            <p className="text-sm font-medium truncate">{sheet.filename || 'sheet'}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => window.open(sheetUrl, '_blank', 'noopener')} data-testid="factory-processing-sheet-download-inline-btn">
            Download
          </Button>
        </div>
      )}
    </div>
  );
};

const FactoryOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);

  // Movement dialog
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveFrom, setMoveFrom] = useState('');
  const [moveTo, setMoveTo] = useState('');
  const [movePieces, setMovePieces] = useState('');
  const [moveNotes, setMoveNotes] = useState('');

  // Request dialog
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [styleNumbers, setStyleNumbers] = useState(['']);
  const [delayReason, setDelayReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  
  // Request chat
  const [chatRequestId, setChatRequestId] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);

  // Acceptance gate
  const [accepting, setAccepting] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await factoryApi.getOrder(id);
      setOrder(data);
    } catch {
      toast.error('Failed to load order');
      navigate('/factory');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // Get quantity for a stage
  const getQty = (stage) => {
    if (!order) return 0;
    if (stage === 'Order Raised') return order.order_raised_quantity || 0;
    if (stage === 'Dispatched') {
      // Include all dispatched pieces — both pending admin signature and accepted (in RFF + downstream).
      // Compute as: pieces_ordered − everything still on the factory side − pieces still in OR.
      const subSum = (order.factory_sub_stages || []).reduce((s, x) => s + (x.quantity || 0), 0);
      const casting = order.stage_quantities?.find(s => s.stage === 'Casting')?.quantity || 0;
      const orQty = order.order_raised_quantity || 0;
      const inv = order.stage_quantities?.find(s => s.stage === 'From Inventory')?.quantity || 0;
      return Math.max(0, (order.pieces_ordered || 0) - orQty - inv - casting - subSum);
    }
    if (FACTORY_SUB_STAGES.includes(stage)) {
      const sub = (order.factory_sub_stages || []).find(s => s.stage === stage);
      return sub?.quantity || 0;
    }
    const sq = order.stage_quantities?.find(s => s.stage === stage);
    return sq?.quantity || 0;
  };

  const openMoveDialog = (fromStage) => {
    setMoveFrom(fromStage);
    const idx = ALL_FACTORY_STAGES.indexOf(fromStage);
    const nextStage = idx >= 0 && idx < ALL_FACTORY_STAGES.length - 1 ? ALL_FACTORY_STAGES[idx + 1] : ALL_FACTORY_STAGES[0];
    setMoveTo(nextStage);
    setMovePieces('');
    setMoveNotes('');
    setMoveDialogOpen(true);
  };

  const handleMove = async () => {
    const pieces = parseInt(movePieces);
    if (!pieces || pieces <= 0) { toast.error('Enter a valid number of pieces'); return; }
    if (pieces > getQty(moveFrom)) { toast.error(`Only ${getQty(moveFrom)} pieces available in ${moveFrom}`); return; }
    setMoving(true);
    try {
      await factoryApi.logMovement(id, { from_stage: moveFrom, to_stage: moveTo, pieces, notes: moveNotes });
      toast.success(`Moved ${pieces} pcs: ${moveFrom} → ${moveTo}`);
      setMoveDialogOpen(false);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move pieces');
    } finally {
      setMoving(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestType) { toast.error('Select a request type'); return; }
    setSubmittingRequest(true);
    try {
      await factoryApi.submitRequest(id, {
        request_type: requestType,
        message: requestMessage,
        style_numbers: requestType === 'Request New Direct Casting Piece(s)' ? styleNumbers.filter(s => s.trim()) : [],
        delay_reason: requestType === 'Delay Expected' ? delayReason : '',
      });
      toast.success('Request submitted');
      setRequestDialogOpen(false);
      setRequestType(''); setRequestMessage(''); setStyleNumbers(['']); setDelayReason('');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleSendChatMessage = async (requestId) => {
    if (!chatMessage.trim()) return;
    setSendingChat(true);
    try {
      await factoryApi.addRequestMessage(id, requestId, { message: chatMessage });
      setChatMessage('');
      fetchOrder();
    } catch { toast.error('Failed to send message'); }
    finally { setSendingChat(false); }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await factoryApi.acceptOrder(id);
      toast.success('Order accepted — you can now begin production');
      fetchOrder();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept order');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please tell admin why you are rejecting this order');
      return;
    }
    setRejecting(true);
    try {
      await factoryApi.rejectOrder(id, { reject_reason: rejectReason.trim() });
      toast.success('Order rejected — admin has been notified');
      setRejectDialogOpen(false);
      setRejectReason('');
      fetchOrder();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject order');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 md:p-6">
        <Skeleton className="h-10 w-48 mb-4" />
        <div className="space-y-4"><Skeleton className="h-40 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>
      </div>
    );
  }
  if (!order) return null;

  // ── Factory acceptance gate ──
  // Pending: show only the summary + Accept/Reject buttons.
  // Rejected: show the rejection reason + "awaiting admin re-assignment".
  // Accepted: full production controls (existing UI).
  const fa = order.factory_acceptance || { status: 'accepted' };
  const isPending = fa.status === 'pending';
  const isRejected = fa.status === 'rejected';
  const isAccepted = !isPending && !isRejected;

  const dispatched = getQty('Dispatched');
  const pct = order.pieces_to_factory > 0 ? Math.round((dispatched / order.pieces_to_factory) * 100) : 0;

  const allStages = [
    { stage: 'Order Raised', qty: getQty('Order Raised') },
    { stage: 'Casting', qty: getQty('Casting') },
    ...FACTORY_SUB_STAGES.map(s => ({ stage: s, qty: getQty(s), isSub: true })),
    { stage: 'Dispatched', qty: dispatched },
  ];

  const movements = (order.movements || []).slice().reverse().slice(0, 20);
  const openRequests = (order.factory_requests || []).filter(r => r.status === 'open').length;

  return (
    <div className="min-h-screen" data-testid="factory-order-detail">
      {/* Header */}
      <div className="sticky top-0 z-30 h-14 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="flex items-center justify-between h-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="flex items-center gap-2 md:gap-3">
            <HomeButton className="h-9 w-9" />
            <Button variant="ghost" size="icon" onClick={() => navigate('/factory')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-base font-bold">{order.order_number}</h1>
              <p className="text-[11px] text-muted-foreground">{order.pieces_to_factory} pcs &middot; {order.gold_karat}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={cn(
              order.priority === 'Urgent' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
              order.priority === 'High' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
              'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400'
            )}>{order.priority}</Badge>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto">
        {/* ── Factory Acceptance Gate ── */}
        {isPending && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500 text-white flex items-center justify-center animate-pulse">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="font-heading text-base text-amber-900 dark:text-amber-200">Please accept this order to start</CardTitle>
                  <CardDescription className="text-amber-700 dark:text-amber-300 text-xs">
                    Confirm you have received the brief, or reject with a reason for admin.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary — only what factory needs to verify before accepting */}
              <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-white/60 dark:bg-background/40 p-3 space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">CO Number</p>
                    <p className="font-bold" data-testid="summary-co-number">{order.co_number || order.order_number}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Pieces</p>
                    <p className="font-bold tabular-nums" data-testid="summary-pieces">{order.pieces_to_factory}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Gold karat</p>
                    <p className="font-bold">{order.gold_karat || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Gold weight</p>
                    <p className="font-bold tabular-nums">{order.gold_weight ? `${order.gold_weight}g` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Diamond weight</p>
                    <p className="font-bold tabular-nums">{order.diamond_weight ? `${order.diamond_weight}ct` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Cobranding</p>
                    <p className="font-bold">{order.cobranding ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Casting deadline</p>
                    <p className="font-semibold text-sm">{order.casting_deadline ? fmtDate(order.casting_deadline) : 'Not set'}</p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground border-t border-amber-100 dark:border-amber-500/20 pt-2">
                  Customer details, materials checklist, and movement controls are unlocked after you accept.
                </p>
              </div>

              {/* Manufacturing Order Form — required before accept */}
              <ManufacturingFormDownload mf={order.manufacturing_form} sheet={order.factory_processing_sheet} orderId={order.id} variant="banner" />

              <div className="flex flex-col md:flex-row gap-2">
                <Button
                  onClick={handleAccept}
                  disabled={accepting || rejecting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11"
                  data-testid="factory-accept-btn"
                >
                  {accepting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Accept &amp; start order
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRejectDialogOpen(true)}
                  disabled={accepting || rejecting}
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/10 h-11"
                  data-testid="factory-reject-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject &amp; notify admin
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isRejected && (
          <Card className="border-red-300 bg-red-50 dark:bg-red-500/10 dark:border-red-500/40">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-red-500 text-white flex items-center justify-center">
                  <X className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="font-heading text-base text-red-900 dark:text-red-200">Order rejected — awaiting admin</CardTitle>
                  <CardDescription className="text-red-700 dark:text-red-300 text-xs">
                    No updates can be made until admin re-sends this order to you.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-white/60 dark:bg-background/40 p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Your rejection reason</p>
                <p className="text-sm whitespace-pre-wrap" data-testid="rejection-reason-text">{fa.reject_reason || '—'}</p>
                {fa.decided_at && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Rejected on {fmtDate(fa.decided_at)} {fmtTime(fa.decided_at)} by {fa.decided_by || 'you'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Production UI — only visible after acceptance ── */}
        {isAccepted && <>

        {/* Manufacturing Order Form + Processing Sheet — always downloadable post-acceptance */}
        {(order.manufacturing_form || order.factory_processing_sheet) && (
          <ManufacturingFormDownload mf={order.manufacturing_form} sheet={order.factory_processing_sheet} orderId={order.id} variant="inline" />
        )}

        {/* Progress Bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Overall Progress</span>
            <span className="text-sm font-bold tabular-nums">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2.5" />
          <p className="text-[11px] text-muted-foreground mt-1">{dispatched} of {order.pieces_to_factory} pieces completed</p>
        </div>

        {/* Deadlines */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <DeadlineBanner label="Casting Deadline" iso={order.casting_deadline} completed={order.casting_completed} />
          <DeadlineBanner label="Delivery Deadline" iso={order.delivery_deadline} completed={order.delivery_completed} />
        </div>

        {/* Materials Status */}
        <div>
          <h2 className="font-heading text-sm font-bold mb-2">Materials Received</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <MaterialCard label="Gold" received={order.gold_received} icon={Scale} color="amber" />
            <MaterialCard label="Diamonds" received={order.diamonds_received} icon={Gem} color="sky" />
            <MaterialCard label="Chains" received={order.chains_received} icon={Link2} color="violet" />
          </div>
        </div>

        {/* Deadline (admin-set factory deadline) */}
        {order.delivery_deadline ? (() => {
          const daysLeft = Math.ceil((new Date(order.delivery_deadline) - new Date()) / 86400000);
          const isOverdue = daysLeft < 0;
          const isUrgent = daysLeft >= 0 && daysLeft <= 3;
          return (
            <div className={cn("p-3 rounded-lg border flex items-center justify-between",
              isOverdue ? "bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30" :
              isUrgent ? "bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30" :
              "bg-muted/30 border-border"
            )}>
              <div className="flex items-center gap-2">
                <Calendar className={cn("h-4 w-4", isOverdue ? "text-red-600" : isUrgent ? "text-amber-600" : "text-muted-foreground")} />
                <span className="text-sm font-medium">Deadline: {fmtDate(order.delivery_deadline)}</span>
              </div>
              <Badge className={cn("text-xs",
                isOverdue ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" :
                isUrgent ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400" :
                "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400"
              )}>
                {isOverdue ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Due today' : `${daysLeft}d left`}
              </Badge>
            </div>
          );
        })() : (
          <div className="p-3 rounded-lg border bg-muted/30 border-border flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            No deadline set
          </div>
        )}

        {/* Pending admin signatures (for awareness) */}
        {order.pending_receipts?.filter(r => r.status === 'pending').length > 0 && (
          <Card className="border-amber-300 dark:border-amber-500/40 bg-amber-50/50 dark:bg-amber-500/10">
            <CardContent className="py-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <p className="font-semibold text-sm text-amber-800 dark:text-amber-300">
                  {order.pending_receipts.filter(r => r.status === 'pending').reduce((sum, r) => sum + (r.pieces || 0), 0)} pcs awaiting admin signature
                </p>
              </div>
              <div className="space-y-1">
                {order.pending_receipts.filter(r => r.status === 'pending').map(r => (
                  <p key={r.id} className="text-[11px] text-amber-700 dark:text-amber-400">
                    • {r.pieces} pcs from {r.from_substage} — dispatched by {r.dispatched_by}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recently rejected receipts (back in Finished Goods) */}
        {order.pending_receipts?.filter(r => r.status === 'rejected').slice(-3).length > 0 && (
          <Card className="border-red-300 dark:border-red-500/40 bg-red-50/50 dark:bg-red-500/10">
            <CardContent className="py-3">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <p className="font-semibold text-sm text-red-800 dark:text-red-300">Rejected Dispatches (returned to Finished Goods)</p>
              </div>
              {order.pending_receipts.filter(r => r.status === 'rejected').slice(-3).map(r => (
                <p key={r.id} className="text-[11px] text-red-700 dark:text-red-400">
                  • {r.pieces} pcs rejected by {r.resolved_by}{r.resolved_notes ? ` — "${r.resolved_notes}"` : ''}
                </p>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Production Pipeline */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <MoveRight className="h-5 w-5 text-amber-500" />
              <div>
                <CardTitle className="font-heading text-base">Production Pipeline</CardTitle>
                <CardDescription className="text-xs">Tap a stage to move pieces forward</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {allStages.map((s) => {
              const colors = STAGE_COLORS[s.stage] || STAGE_COLORS['Order Raised'];
              const canMove = s.qty > 0 && s.stage !== 'Dispatched';

              return (
                <div
                  key={s.stage}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border transition-all",
                    s.qty > 0 ? `${colors.bg} ${colors.ring} ring-1` : "bg-muted/20 border-transparent",
                    canMove && "cursor-pointer hover:shadow-sm hover:scale-[1.005]"
                  )}
                  onClick={() => canMove && openMoveDialog(s.stage)}
                  data-testid={`stage-${s.stage.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {s.isSub && <span className="w-3 border-l-2 border-b-2 border-border h-3 ml-2 rounded-bl" />}
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-semibold", s.qty > 0 ? colors.text : "text-muted-foreground/50")}>{STAGE_LABEL[s.stage] || s.stage}</p>
                  </div>
                  <span className={cn("text-lg font-bold tabular-nums min-w-[32px] text-right", s.qty > 0 ? colors.text : "text-muted-foreground/30")}>{s.qty}</span>
                  {canMove && (
                    <div className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 px-2 py-1 rounded">
                      <ArrowRight className="h-3 w-3" />
                      <span>Move</span>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Requests Section */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-red-500" />
              <div>
                <CardTitle className="font-heading text-base">Requests</CardTitle>
                <CardDescription className="text-xs">
                  {openRequests > 0 ? `${openRequests} open` : 'Raise concerns or requests'}
                </CardDescription>
              </div>
            </div>
            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10" onClick={() => setRequestDialogOpen(true)} data-testid="raise-request-btn">
              <Plus className="h-3 w-3 mr-1" /> New Request
            </Button>
          </CardHeader>
          <CardContent>
            {(order.factory_requests || []).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No requests raised yet</p>
            ) : (
              <div className="space-y-3">
                {[...order.factory_requests].reverse().map(req => (
                  <div key={req.id} className={cn("p-3 rounded-lg border",
                    req.status === 'open' ? "border-red-200 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/5" :
                    "border-emerald-200 bg-emerald-50/50 dark:border-emerald-500/30 dark:bg-emerald-500/5"
                  )}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-sm font-semibold">{req.request_type}</span>
                        <Badge className={cn("ml-2 text-[10px]",
                          req.status === 'open' ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" :
                          "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                        )}>{req.status}</Badge>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{fmtDate(req.created_at)}</span>
                    </div>
                    {req.style_numbers?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {req.style_numbers.map((s, i) => <Badge key={i} variant="outline" className="text-[10px]">Style: {s}</Badge>)}
                      </div>
                    )}
                    {/* Chat messages */}
                    {(req.messages || []).length > 0 && (
                      <div className="space-y-1.5 mb-2 max-h-[150px] overflow-y-auto">
                        {req.messages.map(msg => (
                          <div key={msg.id} className={cn("p-2 rounded text-xs",
                            msg.sender_role === 'factory' ? "bg-white dark:bg-neutral-900 ml-0 mr-8" : "bg-blue-50 dark:bg-blue-500/10 ml-8 mr-0"
                          )}>
                            <div className="flex justify-between">
                              <span className="font-semibold">{msg.sender_name}</span>
                              <span className="text-muted-foreground text-[10px]">{fmtDate(msg.timestamp)}</span>
                            </div>
                            <p className="mt-0.5">{msg.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {req.status === 'open' && (
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="Type a message..."
                          value={chatRequestId === req.id ? chatMessage : ''}
                          onFocus={() => setChatRequestId(req.id)}
                          onChange={(e) => { setChatRequestId(req.id); setChatMessage(e.target.value); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleSendChatMessage(req.id)}
                          className="h-8 text-xs"
                        />
                        <Button size="sm" className="h-8 px-2" disabled={sendingChat || !chatMessage.trim()} onClick={() => handleSendChatMessage(req.id)}>
                          <Send className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {req.status === 'resolved' && req.resolved_by && (
                      <p className="text-[10px] text-emerald-600 mt-1">Resolved by {req.resolved_by}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Timeline */}
        {movements.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="font-heading text-base">Recent Activity</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="space-y-1.5">
                  {movements.map((m, i) => {
                    const isRequest = m.movement_type === 'factory_request';
                    const isResolved = m.movement_type === 'factory_request_resolved';
                    return (
                      <div key={m.id || i} className={cn(
                        "flex items-center justify-between p-2 rounded-lg text-sm",
                        isRequest ? "bg-red-50 dark:bg-red-500/10" : isResolved ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-muted/50"
                      )}>
                        <div>
                          {isRequest ? (
                            <span className="font-medium text-red-600 dark:text-red-400">Request: {m.request_type || m.notes}</span>
                          ) : isResolved ? (
                            <span className="font-medium text-emerald-600 dark:text-emerald-400">{m.notes}</span>
                          ) : (
                            <>
                              <span className="font-medium">{m.pieces} pcs</span>
                              <span className="text-muted-foreground mx-1">:</span>
                              <span className="text-muted-foreground">{STAGE_LABEL[m.from_stage] || m.from_stage} → {STAGE_LABEL[m.to_stage] || m.to_stage}</span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{fmtDate(m.date)}</span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
        </>}
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(o) => !rejecting && setRejectDialogOpen(o)}>
        <DialogContent className="max-w-md" data-testid="factory-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reject this order</DialogTitle>
            <DialogDescription>
              Admin will be notified and will need to re-send the order to you before you can act on it again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs">Reason for rejection *</Label>
            <Textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Pieces count mismatch with sample, gold weight on the order doesn't match the brief, etc."
              data-testid="factory-reject-reason"
            />
            <p className="text-[11px] text-muted-foreground">Be specific — admin will see this exact text on the order timeline.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={rejecting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="factory-reject-submit"
            >
              {rejecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
              Reject order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Move Pieces</DialogTitle>
            <DialogDescription>{STAGE_LABEL[moveFrom] || moveFrom} → {STAGE_LABEL[moveTo] || moveTo}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-muted/50 border flex justify-between items-center">
              <span className="text-sm font-medium">Available in {STAGE_LABEL[moveFrom] || moveFrom}</span>
              <span className="text-lg font-bold tabular-nums">{getQty(moveFrom)}</span>
            </div>
            <div className="space-y-2">
              <Label>To Stage *</Label>
              <Select value={moveTo} onValueChange={setMoveTo}>
                <SelectTrigger data-testid="move-to-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_FACTORY_STAGES.filter(s => s !== moveFrom).map(s => (
                    <SelectItem key={s} value={s}>{STAGE_LABEL[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pieces to Move *</Label>
              <Input
                type="number" min="1" max={getQty(moveFrom)}
                value={movePieces} onChange={(e) => setMovePieces(e.target.value)}
                placeholder={`Max: ${getQty(moveFrom)}`}
                data-testid="move-pieces-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={moveNotes} onChange={(e) => setMoveNotes(e.target.value)} placeholder="Any notes..." rows={2} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)} disabled={moving}>Cancel</Button>
            <Button onClick={handleMove} disabled={moving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-move-btn">
              {moving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MoveRight className="h-4 w-4 mr-1" />}
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Raise a Request</DialogTitle>
            <DialogDescription>Notify the production team about a concern</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Request Type *</Label>
              <Select value={requestType} onValueChange={(v) => { setRequestType(v); setStyleNumbers(['']); setDelayReason(''); }}>
                <SelectTrigger data-testid="request-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            {requestType === 'Request New Direct Casting Piece(s)' && (
              <div className="space-y-2">
                <Label>Style Numbers</Label>
                {styleNumbers.map((sn, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={sn} onChange={(e) => { const n = [...styleNumbers]; n[i] = e.target.value; setStyleNumbers(n); }} placeholder={`Style #${i + 1}`} className="flex-1" />
                    {styleNumbers.length > 1 && <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setStyleNumbers(styleNumbers.filter((_, j) => j !== i))}><X className="h-3 w-3" /></Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setStyleNumbers([...styleNumbers, ''])} className="w-full"><Plus className="h-3 w-3 mr-1" /> Add Style Number</Button>
              </div>
            )}
            {requestType === 'Delay Expected' && (
              <div className="space-y-2">
                <Label>Reason for delay</Label>
                <Textarea value={delayReason} onChange={(e) => setDelayReason(e.target.value)} placeholder="Explain delay reason..." rows={2} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea value={requestMessage} onChange={(e) => setRequestMessage(e.target.value)} placeholder="Any additional details..." rows={2} data-testid="request-message-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitRequest} disabled={submittingRequest} className="bg-red-500 hover:bg-red-600 text-white" data-testid="submit-request-btn">
              {submittingRequest ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FactoryOrderDetail;
